import { inject, Injectable, Injector } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { TOKEN_REFRESH_SERVICE } from '../tokens/token-refresh.token';
import { API_CONFIG } from '../config/api.config';
import { authFlowDebug } from '../utils/auth-flow-debug.util';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private readonly injector = inject(Injector);

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 403) {
          authFlowDebug.logHttpError('http_403_detected', 'ErrorInterceptor', error, {
            method: req.method,
            path: req.url,
          });
        }

        if (
          error.status === 401 &&
          !req.url.includes(API_CONFIG.auth.refresh) &&
          !req.url.includes(API_CONFIG.auth.login) &&
          !req.url.includes(API_CONFIG.auth.logout)
        ) {
          authFlowDebug.logHttpError('http_401_detected', 'ErrorInterceptor', error, {
            method: req.method,
            path: req.url,
            action: 'attempting_token_refresh',
          });
          const tokenRefreshService = this.injector.get(TOKEN_REFRESH_SERVICE);
          return tokenRefreshService.refreshTokens().pipe(
            switchMap((token) => {
              const headers = req.headers.set('Authorization', `Bearer ${token}`);
              const authReq = req.clone({ headers });
              return next.handle(authReq);
            }),
            catchError((refreshErr) => {
              authFlowDebug.logHttpError(
                'token_refresh_failed',
                'ErrorInterceptor.refreshRetry',
                refreshErr,
                { originalPath: req.url },
              );
              return throwError(() => refreshErr);
            })
          );
        }

        if (error.status >= 400) {
          authFlowDebug.logHttpError('http_error', 'ErrorInterceptor', error, {
            method: req.method,
            path: req.url,
          });
        }

        return throwError(() => error);
      })
    );
  }
}
